import { Box, Button, Grid, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery } from '@mui/material'
import { BlockOutlined, SearchOutlined } from '@mui/icons-material'
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display'
import { TableActionButtons } from '../../../presentation-components/miscellaneous-components/table-action-buttons'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import { LookUpDefaultActivityDialog } from './look-up-default-activity-dialog'
import { useNavigate } from 'react-router-dom'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model'
import { useContext, useEffect, useRef, useState } from 'react'
import ContactSupplierService from '../../../../shared/services/contact-supplier/contact-supplier.service'
import { IContactSupplierDetails } from '../../../../shared/services/contact-supplier/contact-supplier.model'
import gaiaTheme from '../../../../root-components/theme'
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods'
import { SearchBar } from '../../../presentation-components/display-elements-components/search-bar'
import { LoadingPage } from '../../loading-page'
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container'
import { a11yProps } from '../../../../shared/utils/misc-shared-utils'
import { segmentedToggleSx } from '../../../../shared/utils/segmented-toggle-sx'
import { CustomTabPanel } from '../../../presentation-components/display-elements-components/custom-tab-panel'
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading'
import { IconName } from '../../../../shared/interfaces/icon-picker.model'
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels'
import { useDebounce } from '../../../../hooks/use-debounce-hook'
import { UserDetailsContext } from '../../../../context/user-details/user-details'
import { EmissionsTotalCell, SpendTotalCell } from '../../../presentation-components/miscellaneous-components/period-total-cells'

export const OrganisationSuppliersPage = () => {
  const navigate = useNavigate();
  //Services
  const contactSupplierService = ContactSupplierService.getInstance();
  //Context
  const reportPeriodContext = useContext(ReportPeriodContext)
  const screenLabelsContext = useContext(ScreenLabelsContext)
  const userNotificationsContext = useContext(UserNotificationsContext)
  // Period totals show the organisation's default Scope 2 basis, as the group cards do.
  const defaultMarketBased = useContext(UserDetailsContext).userDetails?.organisation?.defaultMarketBased
  //State
  const [suppliers, setSuppliers] = useState<IContactSupplierDetails[]>()
  const [searchQuery, setSearchQuery] = useState('')
  const [value, setValue] = useState(0);
  //Look-up default activity
  const [noFactorFilter, setNoFactorFilter] = useState(false)
  const [lookupDialogOpen, setLookupDialogOpen] = useState(false)
  //Table State
  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  //Hooks
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  //Ref
  const requestId = useRef<number>(0)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  useEffect(() => {
    requestId.current++
    const currentRequestId = requestId.current
    contactSupplierService.getAllSuppliersWithPagination(value === 0 ? true : false, true, reportPeriodContext.lastSelectedReportId ?? null, searchQuery, inputPage, perPage, orderBy, order, noFactorFilter, true)
      .then(response => {
        if (currentRequestId === requestId.current) {
          setIsLoading(false)
          setTotalRecords(response.count);
          setSuppliers(response.rows)
        }
      })
      .catch(err => {
        if (currentRequestId === requestId.current) {
          setIsLoading(false)
        }
        console.log(err)
      })
  }, [reportPeriodContext.lastSelectedReportId, inputPage, perPage, change])

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [debouncedSearchQuery, order, orderBy, value, noFactorFilter])

  //Table Contents
  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Action', sortable: false },
    { id: 'name', numeric: false, label: screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' }), sortable: true, isHeading: true },
    { id: 'contactSupplierReference', numeric: false, label: 'Reference(s)', sortable: true },
    { id: 'category', numeric: false, label: 'Default Spend Activity', sortable: true },
    // Period totals for the selected report period (attached by the API, so not sortable server-side).
    { id: 'totalEmissions', numeric: true, label: 'Emissions', sortable: false, align: 'right' },
    { id: 'spendTotals', numeric: true, label: 'Spend', sortable: false, align: 'right' },
    // { id: 'progress', numeric: false, label: 'Data Quality', sortable: false, isSubHeading: true },
  ];

  const rows = !suppliers ? [] : suppliers.map(supplier => {
    return {
      id: supplier.id,
      name: supplier.name,
      contactSupplierReference: supplier.contactSupplierReference ? supplier.contactSupplierReference.split(',').join(', ') : null,
      category: supplier.spendBasedConversion ? `${supplier.spendBasedConversion.localAuthorityCustomIdentifier ? `Code ${supplier.spendBasedConversion.localAuthorityCustomIdentifier}: ` : ''} ${supplier.spendBasedConversion.activity}` : '',
      totalEmissions: <EmissionsTotalCell totals={supplier.totalEmissions} defaultMarketBased={defaultMarketBased} />,
      spendTotals: <SpendTotalCell totals={supplier.spendTotals} />,
      action: (
        <TableActionButtons
          id={supplier.id}
          includeEdit={true}
          includeDeactivate={value === 0}
          onEdit={() => openSupplier(supplier.id)}
          onDeactivate={() => deactivateSuppliers([supplier.id])}
        />
      ),
    }
  });

  //Table Event Handlers
  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  const supplierTerm = screenLabelsContext.getLabel('supplier', { plural: false })
  const suppliersTerm = screenLabelsContext.getLabel('supplier', { plural: true })

  const openSupplier = (id: string) => navigate(`/organisation/suppliers/${id}`)

  const deactivateSuppliers = async (ids: string[]) => {
    const confirmed = await userNotificationsContext.openDialog({
      active: true,
      title: `Deactivate ${ids.length === 1 ? supplierTerm : suppliersTerm}`,
      message: ids.length === 1
        ? `Deactivate this ${supplierTerm}? It will move to the Inactive tab.`
        : `Deactivate ${ids.length} ${suppliersTerm}? They will move to the Inactive tab.`,
      buttonCopy: 'Deactivate', cancelButtonCopy: 'Cancel',
    })
    if (confirmed !== true) return
    const results = await Promise.allSettled(ids.map(id => contactSupplierService.makeSupplierInactive(id)))
    const ok = results.filter(r => r.status === 'fulfilled').length
    userNotificationsContext.openSnackbar({ open: true, message: `Deactivated ${ok} ${ok === 1 ? supplierTerm : suppliersTerm}` })
    setSelected([]); setChange(change + 1)
  }

  //Component Event Handlers
  const changeTabHandler = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setIsLoading(true)
    setSearchQuery('')
    setNoFactorFilter(false)
    setSelected([])
  };

  const toggleNoFactorFilter = (checked: boolean) => {
    setNoFactorFilter(checked)
    setSelected([])
  }

  const onLookupApplied = () => {
    setSelected([])
    setChange(change + 1)
  }

  return (
    <PageOuterContainer>
      <PageHeading
        title={screenLabelsContext.getLabel('supplier', { plural: true, capitalize: 'first' })}
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => navigate('/organisation/suppliers/add-new-supplier')}
        actionButtonText={`Add ${screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' })}`}
        hasTabs={true}
        includeSecondaryActionButton={true}
        secondaryActionButtonIcon={IconName.UploadOutlined}
        secondaryActionButtonHandler={() => navigate('/settings/data-upload/import-data?dataType=suppliers&start=upload')}
        secondaryActionButtonText='Upload Data'
      />
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={changeTabHandler} aria-label="basic tabs example">
            <Tab label="Active" {...a11yProps(0)} />
            <Tab label="Inactive" {...a11yProps(1)} />
          </Tabs>
        </Box>
        <CustomTabPanel value={value} index={0}>
          <Grid container sx={{ mb: 2 }} alignItems="center" justifyContent="space-between" rowSpacing={1} columnSpacing={2}>
            <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <ToggleButtonGroup
                value={noFactorFilter ? 'noFactor' : 'all'}
                exclusive
                size='small'
                onChange={(_event, value) => { if (value) toggleNoFactorFilter(value === 'noFactor') }}
                aria-label='Filter suppliers'
                sx={segmentedToggleSx}
              >
                <ToggleButton value='all'>All</ToggleButton>
                <ToggleButton value='noFactor'>No default activity</ToggleButton>
              </ToggleButtonGroup>
              <Box sx={{ width: { xs: '100%', sm: 280 } }}>
                <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
              </Box>
            </Grid>
            {selected.length > 0 &&
              <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {noFactorFilter &&
                  <Button variant='contained' startIcon={<SearchOutlined fontSize='small' />} onClick={() => setLookupDialogOpen(true)}>
                    <Typography sx={{ pl: 0.5 }}>Look up default activity ({selected.length})</Typography>
                  </Button>}
                {value === 0 &&
                  <Button variant='outlined' startIcon={<BlockOutlined fontSize='small' />} onClick={() => deactivateSuppliers(selected)}>
                    <Typography sx={{ pl: 0.5 }}>Deactivate ({selected.length})</Typography>
                  </Button>}
              </Grid>}
          </Grid>
          <LoadingPage pageReady={!isLoading}>
            <TableDisplay
              onOrderChange={handleOrderChange}
              isMobileMode={isMobileMode}
              headCells={headCells}
              rows={rows}
              onRowClick={openSupplier}
              haveCheckboxSelect={true}
              selectByCheckboxOnly={true}
              selected={selected}
              setSelected={setSelected}
              inputPage={inputPage}
              setInputPage={setInputPage}
              totalRecords={totalRecords}
              perPage={perPage}
              setPerPage={setPerPage} />
          </LoadingPage>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <Grid container sx={{ mb: 2 }}>
            <Grid
              item
              xl={4} lg={4} md={6} sm={12} xs={12}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                width: '100%'
              }}>
              <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
            </Grid>
          </Grid>
          <LoadingPage pageReady={!isLoading}>
            <TableDisplay
              onOrderChange={handleOrderChange}
              isMobileMode={isMobileMode}
              headCells={headCells}
              rows={rows}
              onRowClick={openSupplier}
              haveCheckboxSelect={true}
              selectByCheckboxOnly={true}
              selected={selected}
              setSelected={setSelected}
              inputPage={inputPage}
              setInputPage={setInputPage}
              totalRecords={totalRecords}
              perPage={perPage}
              setPerPage={setPerPage} />
          </LoadingPage>
        </CustomTabPanel>
      </Box>
      <LookUpDefaultActivityDialog
        open={lookupDialogOpen}
        onClose={() => setLookupDialogOpen(false)}
        supplierIds={selected.length > 0 ? selected : undefined}
        onApplied={onLookupApplied}
      />
    </PageOuterContainer>
  )
}